import type { AnyEventObject } from 'xstate';
import { getListingActivationPreview } from '~/features/employer/billing/api';
import type {
  ListingActivationPreview,
  ListingActivationResult,
  ListingFeeKind,
} from '~/features/employer/billing/types';
import { ALERT_VIEW_STATE, AppActorSystem } from '~/features/xstateSystem';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { MARKET_READ_TIMEOUT_MS } from '../requestPolicy';

/** Read the fee before publishing. An unavailable preview must not authorize a charge. */
export const previewListingFee = async (
  kind: ListingFeeKind,
  listingId: number | undefined,
  signal?: AbortSignal
): Promise<ListingActivationPreview | null> => {
  if (listingId == null) throw new Error("Couldn't confirm the listing fee. Please try again.");
  try {
    const preview = await withRequestDeadline(
      (deadlineSignal) => getListingActivationPreview(kind, listingId, deadlineSignal),
      MARKET_READ_TIMEOUT_MS,
      signal
    );
    if (!preview || typeof preview.chargeDue !== 'boolean') {
      throw new Error('Listing fee preview is unavailable.');
    }
    return preview;
  } catch (err) {
    console.error('Failed to preview the listing fee', err);
    throw new Error("Couldn't confirm the listing fee. Please try again.");
  }
};

/** Only an explicit `chargeDue` interrupts publishing; every other reason activates directly. */
export const listingFeeIsDue = (preview: ListingActivationPreview | null | undefined) =>
  preview?.chargeDue === true;

/**
 * Used when the server answers 402 on activate although the preview showed no charge (a race
 * with the flag or with a lapsed window): the dialog still needs something to show.
 */
export const fallbackListingFeePreview = (
  paymentMethodOnFile: boolean
): ListingActivationPreview => ({
  listingFeeBillingEnabled: true,
  chargeDue: true,
  reason: 'STANDARD',
  amountCents: 1000,
  windowStart: null,
  windowEnd: null,
  paymentMethodOnFile,
  plan: null,
});

export const POSSIBLE_DUPLICATE_ALERT = {
  text: 'Possible duplicate listing',
  subText:
    "This looks like a duplicate of another active listing. Check that you aren't posting the same opportunity twice.",
} as const;

/** Non-blocking: the listing is already active, this only asks the employer to double-check. */
export const warnPossibleDuplicates = ({
  event,
  system,
}: {
  event: AnyEventObject;
  system: AppActorSystem;
}) => {
  const ids = (event['output'] as ListingActivationResult | undefined)?.possibleDuplicateListingIds;
  if (!Array.isArray(ids) || ids.length === 0) return;
  system.get(ALERT_VIEW_STATE)?.send({
    type: 'ON_SHOW_ALERT',
    payload: { ...POSSIBLE_DUPLICATE_ALERT, type: 'warning', timeout: 8000 },
  });
};

/** The fee-specific failure remains actionable even when production hides raw errors. */
export const showFeePreviewError = ({ system }: { system: AppActorSystem }) => {
  system.get(ALERT_VIEW_STATE)?.send({
    type: 'ON_SHOW_ALERT',
    payload: {
      text: "Couldn't confirm the listing fee",
      subText: 'Please try again before publishing.',
      type: 'error',
      timeout: 5000,
    },
  });
};
